import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Pencil, Search } from 'lucide-react';
import { useFacility } from '../context/FacilityContext';
import rentalsService from '../services/rentalsService';
import RentalToolsModal from '../components/RentalToolsModal';
import RentalHoldModal from '../components/RentalHoldModal';
import { exportRowsToCsv } from '../utils/csvExport';
import { toast } from 'sonner';
import MetricDrillDown from '../components/MetricDrillDown';

// Dates render via the shared tz-aware formatter (utils/datetime): bare
// dates never shift, and stored naive-UTC instants render on the FACILITY
// calendar — the local helper here showed the browser's date for
// timestamps, which reads a day ahead every evening (the Claire Allen
// move-out class, 2026-08-01).
import { formatDate as tzFormatDate, formatDateTime } from '../utils/datetime';
import { useTableSort, SortTh } from '../components/SortableTable';

const Rentals = () => {
  const { facilityId, currentFacility, loading: facilityLoading } = useFacility();
  const formatDate = (d) => tzFormatDate(d, currentFacility?.timezone);
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  // Transfer / plan, same shared component the customer row and
  // the rental page mount — one implementation, three surfaces.
  const [toolModal, setToolModal] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  // Status filter pill. 'all' shows every status; the rest match the
  // rental.status values. Applied BEFORE grouping so the unit-type blocks
  // reflect the filter, and combined with the search term.
  const [statusFilter, setStatusFilter] = useState('all');
  // { mode: 'pause' | 'resume', rental } while the hold dialog is open.
  const [holdModal, setHoldModal] = useState(null);
  // Which KPI drill-down is open (null = none). The three summary cards
  // open a shared <MetricDrillDown> listing the rows behind each number.
  const [drillMetric, setDrillMetric] = useState(null);

  const fetchRentals = useCallback(async () => {
    if (!facilityId) {
      if (!facilityLoading) setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = showArchived
        ? await rentalsService.getAllWithArchived(facilityId)
        : await rentalsService.getAll(facilityId);
      setRentals(res.data);
    } catch (error) {
      console.error('Error fetching rentals:', error);
    } finally {
      setLoading(false);
    }
  }, [facilityId, facilityLoading, showArchived]);

  useEffect(() => { fetchRentals(); }, [fetchRentals]);

  const handleEndRental = async (rentalId) => {
    if (!window.confirm('Are you sure you want to end this rental?')) return;
    try {
      await rentalsService.end(facilityId, rentalId);
      fetchRentals();
    } catch (error) {
      console.error('Error ending rental:', error);
      toast.error('Failed to end rental');
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Active': return 'bg-emerald-50 text-emerald-600';
      case 'Ended': return 'bg-slate-50 text-slate-500';
      case 'Delinquent': return 'bg-red-50 text-red-500';
      case 'Lien': return 'bg-orange-50 text-orange-600';
      default: return 'bg-blue-50 text-blue-600';
    }
  };

  // Column sorting. Declared BEFORE the early returns below — hooks must
  // run in the same order on every render (rules-of-hooks).
  const SORT_ACCESSORS = React.useMemo(() => ({
    // Sorts on UNIT only (owner, 2026-08-05) — the column reads
    // "Unit 21 · Kim Pietrucki", and unit is what you scan it by.
    unit_customer: (r) => r.unit_number,
    start_date: (r) => r.start_date,
    paid_to: (r) => r.paid_to,
    billed_to: (r) => r.billed_to,
    rate: (r) => Number(r.monthly_rate || 0),
    balance: (r) => Number(r.balance || 0),
    status: (r) => r.status,
    created_at: (r) => r.created_at,
    updated_at: (r) => r.updated_at,
  }), []);
  // Default: flat list in unit-number order (owner, 2026-08-05). The
  // unit-type BLOCK view now only appears if a future change re-exposes
  // the unsorted state — unit order won over type grouping.
  const { applySort, sortKey, sortDir, toggle } = useTableSort(
    SORT_ACCESSORS, { key: 'unit_customer', dir: 'asc' });

  if (loading || facilityLoading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!facilityId) return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <h2 className="text-lg font-semibold text-slate-900">No facility selected</h2>
      <p className="text-[13px] text-slate-500 mt-1">Create a facility in Settings to manage rentals.</p>
    </div>
  );

  const activeRentals = rentals.filter(r => r.status === 'Active');
  // "Outstanding" mirrors the Dashboard "Past due" definition (and the
  // backend `past_due` breakdown): Delinquent rentals, or Active rentals
  // with a positive balance. Computing it the same way here keeps the
  // headline equal to the drill-down total it opens.
  const pastDueRentals = rentals.filter(
    (r) => r.status === 'Delinquent'
      || (r.status === 'Active' && (r.balance || 0) > 0),
  );

  // Status filter + free-text search by unit number OR customer name
  // (case-insensitive substring), so an operator can locate a rental either
  // way. Both run BEFORE grouping so the unit-type blocks below reflect the
  // active filter.
  const q = searchTerm.trim().toLowerCase();
  const filteredRentals = rentals.filter((r) => {
    // "Paused" is a hold ON a rental, not a status: a paused rental is still
    // Active or Delinquent underneath, so it has its own pill.
    if (statusFilter === 'paused') {
      if (!r.paused_at) return false;
    } else if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (q) {
      const hay = `${r.unit_number || ''} ${r.customer_name || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  // Natural-aware compare so "5x5" < "10x10" < "10x20" and unit "2" < "10".
  const natCmp = (a, b) =>
    String(a ?? '').localeCompare(String(b ?? ''), undefined, { numeric: true, sensitivity: 'base' });

  // A sort answers a cross-cutting question ("who owes the most", "what
  // changed last"), so an ACTIVE sort FLATTENS the unit-type blocks below —
  // sorting inside each block would scatter the answer across headers.
  const sortedRentals = applySort(filteredRentals);

  // Group rentals into BLOCKS by unit type so the operator scans one type at
  // a time. Blocks are natural-sorted by type name (Unassigned last); units
  // within a block are sorted by unit number.
  const groupedRentals = (() => {
    const groups = new Map();
    for (const r of filteredRentals) {
      const key = r.unit_type || 'Unassigned';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(r);
    }
    return [...groups.entries()]
      .map(([type, items]) => ({ type, items: items.slice().sort((a, b) => natCmp(a.unit_number, b.unit_number)) }))
      .sort((a, b) => {
        if (a.type === 'Unassigned') return 1;
        if (b.type === 'Unassigned') return -1;
        return natCmp(a.type, b.type);
      });
  })();

  return (
    <div>
      {/* Sub-header strip (no duplicate title — parent RentalsPage owns
          the H1 + New Rental button). Just shows the row count and the
          show-archived toggle. */}
      <div className="flex justify-between items-center mb-4 text-[13px] text-slate-500">
        <span>
          {q || statusFilter !== 'all'
            ? `${filteredRentals.length} of ${rentals.length} rental${rentals.length === 1 ? '' : 's'}`
            : `${rentals.length} total rental${rentals.length === 1 ? '' : 's'}`}
        </span>
        <span className="flex items-center gap-3">
          <button
            onClick={() => exportRowsToCsv(
              `rentals-${new Date().toISOString().slice(0, 10)}`,
              filteredRentals,
              [
                { key: 'unit_number', label: 'Unit' },
                { key: 'customer_name', label: 'Customer' },
                { key: 'unit_type', label: 'Type' },
                { key: 'status', label: 'Status' },
                { key: 'monthly_rate', label: 'Rate' },
                { key: 'balance', label: 'Balance' },
                { key: 'start_date', label: 'Start' },
                { key: 'paid_to', label: 'Paid To' },
                { key: 'billed_to', label: 'Bill To' },
              ],
            )}
            className="text-[12px] font-medium text-white rounded-md px-2.5 py-1 bg-blue-600 hover:bg-blue-700"
          >
            Export CSV
          </button>
          <label className="flex items-center gap-1.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="rounded border-slate-300"
            />
            Show archived
          </label>
        </span>
      </div>

      {/* Search by unit number or customer name (mirrors the Customers
          page search). Filters client-side over the denormalized
          unit_number + customer_name the list endpoint already returns. */}
      <div className="mb-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search by unit # or customer name..."
            className="w-full pl-9 pr-4 py-2 rounded-lg border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Status filter pills. Applied before grouping (see filteredRentals)
          and combined with the search above. */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {[
          { key: 'all', label: 'All' },
          { key: 'Active', label: 'Active' },
          { key: 'Delinquent', label: 'Delinquent' },
          { key: 'Ended', label: 'Ended' },
          { key: 'Lien', label: 'Lien' },
          { key: 'paused', label: `Paused (${rentals.filter((x) => x.paused_at).length})` },
        ].map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setStatusFilter(f.key)}
            className={`px-3 py-1.5 rounded-full text-[12px] font-medium transition-colors ${
              statusFilter === f.key
                ? 'bg-blue-600 text-white'
                : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <button
          type="button"
          onClick={() => setDrillMetric('active_rentals')}
          aria-label="Show the active rentals list"
          className="text-left rounded-xl border border-slate-200/80 bg-white p-4 cursor-pointer transition-colors hover:bg-slate-50 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
        >
          <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">Active Rentals</p>
          <p className="text-xl font-semibold text-slate-900 mt-1">{activeRentals.length}</p>
        </button>
        <button
          type="button"
          onClick={() => setDrillMetric('monthly_revenue')}
          aria-label="Show the active rentals whose rates sum to monthly revenue"
          className="text-left rounded-xl border border-slate-200/80 bg-white p-4 cursor-pointer transition-colors hover:bg-slate-50 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
        >
          <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">Monthly Revenue</p>
          <p className="text-xl font-semibold text-emerald-600 mt-1">
            ${activeRentals.reduce((s, r) => s + r.monthly_rate, 0).toFixed(2)}
          </p>
        </button>
        <button
          type="button"
          onClick={() => setDrillMetric('past_due')}
          aria-label="Show the rentals with an outstanding balance"
          className="text-left rounded-xl border border-slate-200/80 bg-white p-4 cursor-pointer transition-colors hover:bg-slate-50 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
        >
          <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">Outstanding</p>
          <p className="text-xl font-semibold text-red-600 mt-1">
            ${pastDueRentals.reduce((s, r) => s + (r.balance || 0), 0).toFixed(2)}
          </p>
        </button>
      </div>

      <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50/50 border-b border-slate-100">
                {[
                  { k: 'unit_customer', label: 'Unit · Customer' },
                  { k: 'start_date', label: 'Start Date' },
                  { k: 'paid_to', label: 'Paid To' },
                  { k: 'billed_to', label: 'Bill To' },
                  { k: 'rate', label: 'Rate' },
                  { k: 'balance', label: 'Balance' },
                  { k: 'status', label: 'Status' },
                  { k: 'created_at', label: 'Created' },
                  { k: 'updated_at', label: 'Updated' },
                ].map(({ k, label }) => (
                  <SortTh key={k} label={label} sortKey={k} dense
                    current={sortKey} dir={sortDir} onSort={toggle} />
                ))}
                <th className="text-right px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filteredRentals.length === 0 ? (
                <tr><td colSpan="10" className="px-4 py-8 text-center text-[13px] text-slate-400">{q || statusFilter !== 'all' ? 'No rentals match this filter' : 'No rentals found'}</td></tr>
              ) : (
                // An active column sort flattens the unit-type blocks into
                // one ordered list; no sort keeps the blocks.
                (sortKey
                  ? [{ type: null, items: sortedRentals }]
                  : groupedRentals
                ).map((block) => (
                  <React.Fragment key={block.type || '__sorted__'}>
                    {/* Block header: one unit type, then its units sorted by number. */}
                    {block.type && (
                    <tr className="bg-slate-100/60 border-y border-slate-200/70">
                      <td colSpan="10" className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wider">
                        {/* Type name in the active theme/brand color (var(--c-brand)
                            tracks the blue/green/orange switcher + dark mode). */}
                        <span style={{ color: 'var(--c-brand)' }}>{block.type}</span>
                        <span className="ml-2 text-slate-400 font-normal normal-case">{block.items.length} unit{block.items.length === 1 ? '' : 's'}</span>
                      </td>
                    </tr>
                    )}
                    {block.items.map((r) => (
                  <tr key={r._id} className={`hover:bg-slate-50/50 transition-colors ${r.is_archived ? 'opacity-60' : ''}`}>
                    <td className="px-4 py-3 text-[13px] font-medium text-slate-700">
                      {/* Lead with the unit number so a rental is findable by
                          unit at a glance, e.g. "Unit 14 · Kayla Gibson". */}
                      {r.unit_number ? <span className="text-slate-900">Unit {r.unit_number}</span> : <span className="text-slate-400">No unit</span>}
                      <span className="text-slate-400"> · </span>
                      {r.customer_name || 'Unknown'}
                      {r.is_archived && <span className="ml-2 px-1.5 py-0.5 text-[10px] rounded bg-slate-100 text-slate-500">Archived</span>}
                    </td>
                    <td className="px-3 py-3 text-[13px] text-slate-500 whitespace-nowrap">
                      <span className="flex items-center gap-1.5"><Calendar className="h-3 w-3 text-slate-300" />{formatDate(r.start_date)}</span>
                    </td>
                    <td className="px-3 py-3 text-[13px] text-slate-500 whitespace-nowrap">{formatDate(r.paid_to) || '—'}</td>
                    <td className="px-3 py-3 text-[13px] text-slate-500 whitespace-nowrap">{formatDate(r.billed_to) || '—'}</td>
                    <td className="px-3 py-3 text-[13px] font-medium text-slate-700">${r.monthly_rate}</td>
                    <td className={`px-3 py-3 text-[13px] font-semibold whitespace-nowrap ${r.balance > 0 ? 'text-red-500' : 'text-emerald-600'}`}>
                      ${(r.balance || 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${getStatusStyle(r.status)}`}>{r.status}</span>
                      {r.paused_at && (
                        <span
                          className="ml-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-amber-50 text-amber-700"
                          title={`Paused ${formatDate(r.paused_at)} — move-out reported for ${r.move_out_reported_on || '—'}. No billing or messages until it is moved out or resumed.`}
                          data-testid={`rental-paused-${r._id}`}
                        >
                          Paused
                        </span>
                      )}
                    </td>
                    {/* Stored naive-UTC; shown on the FACILITY clock. Date
                        only so 11 columns still fit — exact time on hover. */}
                    <td className="px-3 py-3 text-[12px] text-slate-400 whitespace-nowrap"
                        title={r.created_at ? formatDateTime(r.created_at, currentFacility?.timezone) : ''}>
                      {formatDate(r.created_at) || '—'}
                    </td>
                    <td className="px-3 py-3 text-[12px] text-slate-400 whitespace-nowrap"
                        title={r.updated_at ? formatDateTime(r.updated_at, currentFacility?.timezone) : ''}>
                      {formatDate(r.updated_at) || '—'}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {/* nowrap per LABEL: without it the action labels broke
                          across two lines and doubled row height. */}
                      <div className="flex items-center justify-end gap-2.5 whitespace-nowrap">
                        {/* Pencil icon, not the word — matches the edit
                            affordance on Payments/Invoices rows (owner,
                            2026-08-11). */}
                        <Link to={`/rentals/${r._id}/edit`} title="Edit rental"
                          className="text-blue-600 hover:text-blue-700 transition-colors">
                          <Pencil className="h-3.5 w-3.5" />
                        </Link>
                        {/* "Move Out" is what this is called everywhere else —
                            the unit page, the customer's rentals row, the rental
                            page. "End" was this table's own word for the same
                            action. Archive is gone: ending a rental already
                            frees the unit and moves it out of the active list,
                            so archiving on top of that was a second lifecycle
                            concept with no distinct meaning to an operator. */}
                        {/* OCCUPYING statuses, not just Active. Gating on
                            'Active' hid every tool from Delinquent and Lien
                            rentals — the exact rows that need Write off, a
                            payment Plan, or a Move Out. A delinquent tenant
                            still holds the unit; that is what makes them
                            worth acting on. */}
                        {['Active', 'Delinquent', 'Lien'].includes(r.status) && (
                          <>
                            <button onClick={() => setToolModal({ kind: 'transfer', rental: r })}
                              className="text-[13px] text-slate-500 hover:text-slate-700 font-medium transition-colors">Transfer</button>
                            <button onClick={() => setToolModal({ kind: 'plan', rental: r })}
                              className="text-[13px] text-slate-500 hover:text-slate-700 font-medium transition-colors">Plan</button>
                            {/* No "Write off" — feature fully retired
                                2026-08-06: a forced Move Out voids the open
                                invoices and tags them written_off; for a
                                staying tenant, per-invoice void on the
                                Billing tab is the no-grouping-compliant
                                tool. */}
                            {/* A hold while a reported move-out is checked:
                                Pause stops the billing and the messages;
                                it ends with Move Out or Resume. A rental
                                under a lien cannot be paused (the server
                                refuses) — that process has its own close. */}
                            {r.paused_at ? (
                              <button onClick={() => setHoldModal({ mode: 'resume', rental: r })}
                                className="text-[13px] text-emerald-600 hover:text-emerald-700 font-medium transition-colors">Resume</button>
                            ) : r.status !== 'Lien' && (
                              <button onClick={() => setHoldModal({ mode: 'pause', rental: r })}
                                className="text-[13px] text-amber-600 hover:text-amber-700 font-medium transition-colors">Pause</button>
                            )}
                            <button onClick={() => handleEndRental(r._id)} className="text-[13px] text-rose-600 hover:text-rose-700 font-medium transition-colors">Move Out</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                    ))}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {holdModal && (
        <RentalHoldModal
          mode={holdModal.mode}
          rental={holdModal.rental}
          facilityId={facilityId}
          timeZone={currentFacility?.timezone}
          onClose={() => setHoldModal(null)}
          onDone={fetchRentals}
        />
      )}

      {drillMetric && (
        <MetricDrillDown
          facilityId={facilityId}
          metric={drillMetric}
          onClose={() => setDrillMetric(null)}
        />
      )}
      <RentalToolsModal
        facilityId={facilityId}
        toolModal={toolModal}
        onClose={() => setToolModal(null)}
        onChanged={fetchRentals}
      />
    </div>
  );
};

export default Rentals;
